import React, { useMemo, useEffect, useState } from "react";

import DownloadIcon from '@mui/icons-material/Download';
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from "@mui/material/Typography";
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';

import ListBox, { IOptions } from "@refere/ui-utils/ui/components/ListBox";
import FormModifierWrapper from "@refere/ui-utils/ui/components/FormModifierWrapper"
import { TipoPagamento } from "@refere/ui-utils/ui/components/FormDadosPagamento"

import AvisoMaisDeDoisSegurosAtivos from "../../../componentes/AvisoMaisDeDoisSegurosAtivos";
import ParteCancelamento from "./ParteCancelamento";

import useMediaQuery from "@mui/material/useMediaQuery";
import { useHookstate } from "@hookstate/core";
import { number, object, string } from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { mostraDeDateISOparaDataPtBr } from "@refere/ui-utils/ui/utils/format";

import { globalState } from "../../../../features/global-state";
import {
    downloadPdfCertificado,
    downloadPdfProposta,
    downloadPropostaAssinada,
    efetivarNoSigas,
    getContasCorrentesAilos,
    IDadosPagamentoDBC,
    pagar,
    updatePagamentoStatus,
    uploadPropostaAssinada,
    definirDadosPagamentoDBC,
    IContaCorrente
} from "../../../../features/pages/certificado/certificado";

const ParteRenovacao = () => {
    const certificadoState = useHookstate(globalState.pageCertificado);

    return (
        <>
            <AvisoMaisDeDoisSegurosAtivos />
            <Box>
                <Box
                    mb={4}
                >
                    <Typography pl={2} fontSize="16px">
                        Contratação
                    </Typography>
                    <Divider />
                </Box>
                <Box>
                    {
                        certificadoState.dtHrPagamento?.get() &&
                        <Typography variant="body2" pl={2} mt={4}>
                            Renovação Automática concluída!
                        </Typography>
                    }
                    <Typography variant="body2" mt={2} pl={2}>
                        Imprima o certificado e entregue ao cooperado
                    </Typography>
                </Box>
                <Box pl={2} mt={4}>
                    <Button
                        sx={{
                            textTransform: "none",
                            minWidth: "210px"
                        }}
                        onClick={() => downloadPdfCertificado()}
                        variant="contained"
                    >
                        Gerar certificado
                    </Button>
                </Box>
                <Box pl={2} mt={1}>
                    {
                        certificadoState.dtHrEmissaoCertificado?.get() &&
                        <Typography variant="caption">
                            <span>Status Certificado: emitido em </span>
                            {
                                mostraDeDateISOparaDataPtBr(certificadoState.dtHrEmissaoCertificado?.get({ noproxy: true }), "dd/MM/yyyy HH:mm:ss")
                            }
                        </Typography>
                    }
                </Box>
                <ParteCancelamento />
            </Box>
        </>
    );
}

const schema =  object({
    codTipoPagamento: number()
        .nullable()
        .required("Opção de pagamento é obrigatória"),
    idContaCorrente: string()
        .nullable()
        .when("codTipoPagamento", {
            is: TipoPagamento.DebitoConta,
            then: (s) => s.required("Instituição financeira é obrigatória")
        }),
})

const labelsPagamentos = {
    [TipoPagamento.DebitoConta]: "Débito em Conta Corrente",
    [TipoPagamento.CartaoCredito]: "Cartão de Crédito",
}

const popupCenter = ({
    url, 
    w, 
    h,
    onClose = null
} : {
    url: string 
    w: number,
    h: number,
    onClose?: () => void
}) => {
    // Fixes dual-screen position                             Most browsers      Firefox
    const dualScreenLeft = window.screenLeft !==  undefined ? window.screenLeft : window.screenX;
    const dualScreenTop = window.screenTop !==  undefined   ? window.screenTop  : window.screenY;

    const width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width;
    const height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height;

    const systemZoom = width / window.screen.availWidth;
    const left = (width - w) / 2 / systemZoom + dualScreenLeft
    const top = (height - h) / 2 / systemZoom + dualScreenTop
    const newWindow = window.open(url, "_blank", 
      `
      scrollbars=yes,
      width=${w / systemZoom}, 
      height=${h / systemZoom}, 
      top=${top}, 
      left=${left},
      popup=1
      `
    )

    if (newWindow?.focus) 
        newWindow.focus();

    if (onClose) {
        const timer = setInterval(function() { 
            if(newWindow.closed) {
                clearInterval(timer);
                onClose();
            }
        }, 500);
    }

    return newWindow;
}

let doingOrderStatusCall = false;
let orderStatusIntervalId = null

function usePagamentoDoneChanged() {
    const certificadoState = useHookstate(globalState.pageCertificado);

    const watchOrderStatus = 
        !certificadoState.dtHrPagamento?.get() //não tem uma dt de pagamento
        && !!certificadoState.urlCheckout?.get(); //gerou uma url de checkout

    useEffect(() => {
        if (watchOrderStatus) {
            orderStatusIntervalId = setInterval(async () => {
                if (!doingOrderStatusCall) {
                    doingOrderStatusCall = true;
                    await updatePagamentoStatus(false);
                    doingOrderStatusCall = false;
                }
            }, 5 * 1000);
        }
        else {
            if (orderStatusIntervalId !== null) {
                clearInterval(orderStatusIntervalId);
                orderStatusIntervalId = null;
            }
        }
    }, [ watchOrderStatus ]);
}

const getIdFromContaCorrente = (
    codBanco:string, 
    agencia:string, 
    digitoAgencia:string, 
    conta:string, 
    digitoConta:string
) => {
    return `${codBanco ?? ""}${agencia ?? ""}${digitoAgencia ?? ""}${conta ?? ""}${digitoConta ?? ""}`
}

const handleDigito = (digito:string) => {
    if ([undefined, null].indexOf(digito) > -1) 
        return "";

    return `-${digito}`
}
interface IContaCorrenteForm extends IContaCorrente {
    id?: string
}
interface IFormDadosPagamentoDBC extends  IDadosPagamentoDBC {
    contasCorrentes?: IContaCorrenteForm[]
    idContaCorrente?: string
}

const PassoCertificado = () => {
    const certificadoState = useHookstate(globalState.pageCertificado);

    // Carrega soh parte da fase nesse caso
    if (certificadoState.ehRenovacao?.get()) {
        return <ParteRenovacao />;
    }

    usePagamentoDoneChanged();

    const [ erroGeracaoPagamentoPay2B, setErroGeracaoPagamentoPay2B ] = useState<boolean>(false);
    const [ pesquisandoContasCorrentes, setPesquisandoContasCorrentes ] = useState<boolean>(false)

    const state = useHookstate({
        page: 0,
        rowsPerPage: 5
    } as {
        page: number,
        rowsPerPage: number
    });

    const {
        watch,
        setValue,
        control,
        getValues,
        handleSubmit
    } = useForm<IFormDadosPagamentoDBC>({
        resolver: yupResolver(schema as any),
        mode: "onBlur"
    })

    const tiposPagamentos = useMemo((): IOptions[] => {
        // Caso a state não esteja carregada não mostra opções de pagamento
        // previne piscada na tela de ambas as opções aparecerem por um breve momento quando só uma deveria
        if(!certificadoState.get()) 
            return [];

        // Caso o pagamento por débito tenha sido feito só mostra essa opção de pagamento
        if(certificadoState.dtHrDefinicaoDadosBancariosDBC?.get())
            return [
                {
                    value: TipoPagamento.DebitoConta,
                    label: labelsPagamentos[TipoPagamento.DebitoConta]    
                }
            ]

        // Caso a proposta com cartão de crédito tenha sido gerada só mostra essa opção de pagamento
        if(certificadoState.dtHrImpressaoProposta?.get())
            return [
                {
                    value: TipoPagamento.CartaoCredito,
                    label: labelsPagamentos[TipoPagamento.CartaoCredito]    
                }
            ];

        return certificadoState.tiposPagamento.get().map(tp => ({
            value: tp,
            label: labelsPagamentos[tp]
        }))
    }, [ certificadoState ])

    useEffect(() => {
        setValue("codTipoPagamento", certificadoState.codTipoPagamento?.get())
        setValue("codBanco", certificadoState.codBanco?.get())
        setValue("agencia", certificadoState.agencia?.get())
        setValue("digitoAgencia", certificadoState.digitoAgencia?.get())
        setValue("conta", certificadoState.conta?.get())
        setValue("digitoConta", certificadoState.digitoConta?.get())

    }, [
        globalState.pageCertificado.codTipoPagamento,
        globalState.pageCertificado.codBanco,
        globalState.pageCertificado.agencia,
        globalState.pageCertificado.digitoAgencia,
        globalState.pageCertificado.conta,
        globalState.pageCertificado.digitoConta
    ]);

    useEffect(() => {
        if(
            getValues("codTipoPagamento") === TipoPagamento.DebitoConta &&
            (getValues("contasCorrentes") ?? []).length === 0 &&
            !pesquisandoContasCorrentes
        )
            void loadContasCorrentesAilos()

    }, [ watch("codTipoPagamento"), certificadoState ])

    useEffect(() => {
        const idContaCorrente = getIdFromContaCorrente(
            getValues('codBanco'), 
            getValues('agencia'), 
            getValues('digitoAgencia'), 
            getValues('conta'), 
            getValues('digitoConta')
        )

        // Seta o idContaCorrente só depois de carregar as contas
        if(idContaCorrente && (getValues("contasCorrentes") ?? []).length > 0)
            setValue('idContaCorrente', idContaCorrente)

    }, [ watch("contasCorrentes") ])

    const loadContasCorrentesAilos = async () => {
        setPesquisandoContasCorrentes(true)

        const contasCorrentes = (await getContasCorrentesAilos())
            .map(cc => ({
                ...cc, 
                id:  getIdFromContaCorrente(
                        cc.codBanco,
                        cc.agencia,
                        cc.digitoAgencia,
                        cc.conta,
                        cc.digitoConta
                    )
            }));

        // Verifica se o conta já selecionado esta no array buscado
        // Se não estiver, limpa os campos
        const isContaCorrenteSelecionadaInvalida = !(contasCorrentes.some(cc => 
            cc.id === getIdFromContaCorrente(
                getValues('codBanco'), 
                getValues('agencia'), 
                getValues('digitoAgencia'), 
                getValues('conta'), 
                getValues('digitoConta')
            )
        ));

        if (isContaCorrenteSelecionadaInvalida) {
            setValue("idContaCorrente", null)
            setValue("codBanco", null)
            setValue("conta", null)
            setValue("digitoAgencia", null)
            setValue("agencia", null)
            setValue("digitoConta", null)
        }

        setValue("contasCorrentes", contasCorrentes)
        setPesquisandoContasCorrentes(false)
    }

    const contas = (watch("contasCorrentes") ?? []).map(cc => ({
        label: `${cc.nomeBanco} - Agência ${cc.agencia + handleDigito(cc.digitoAgencia)} - Conta ${cc.conta + handleDigito(cc.digitoConta)}`,
        value: cc.id
    })) 

    const match840 = useMediaQuery("(max-width: 840px)");

    return (
        <>
            <AvisoMaisDeDoisSegurosAtivos />
            <Box>
                <Box
                    mb={4}
                >
                    <Typography pl={2} fontSize="16px">
                        Contratação
                    </Typography>
                    <Divider />
                </Box>
                
                {
                    tiposPagamentos.length > 1 &&
                    <Box
                        mb={1}
                    >
                        <Box 
                            mr={1}
                        >
                            <FormModifierWrapper
                                name="codTipoPagamento"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <ListBox                                
                                        {...field}
                                        label="Selecione a opção de pagamento"
                                        options={tiposPagamentos}
                                        value={field.value}
                                        error={fieldState.invalid}
                                        size="medium"
                                        autoWidthButtonArea
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                }
                    
                <Stack direction="column">
                    {
                        watch("codTipoPagamento") === TipoPagamento.DebitoConta &&
                        <>
                            <Box
                                display="flex"
                                flexDirection={match840 ? "column" : "row"}
                            >
                                <Box
                                    width={match840 ? "100%" : "450px"}
                                    mr={1}
                                >
                                    <FormModifierWrapper
                                        name="idContaCorrente"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <ListBox
                                                {...field}
                                                variant="select"
                                                options={contas}
                                                label="Instituição Financeira"
                                                error={fieldState.invalid}
                                                disabled={!!certificadoState.dtHrDefinicaoDadosBancariosDBC?.get()}
                                                onChange={(e)=> {
                                                    const contaCorrente = getValues("contasCorrentes").find(cc => 
                                                        cc.id === e.target.value
                                                    )
                                                    
                                                    field.onChange(e.target.value)
                                                    setValue('codBanco', contaCorrente?.codBanco)
                                                    setValue('agencia', contaCorrente?.agencia)
                                                    setValue('digitoAgencia', contaCorrente?.digitoAgencia)
                                                    setValue('conta', contaCorrente?.conta)
                                                    setValue('digitoConta', contaCorrente?.digitoConta)
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                                <Box
                                    mb={match840 ? 2 : 0}
                                >
                                    <Button 
                                        onClick={() => loadContasCorrentesAilos()}
                                        disabled={!!certificadoState.dtHrDefinicaoDadosBancariosDBC?.get()}
                                        variant="contained"
                                    >
                                        Buscar Contas
                                    </Button>
                                </Box>
                            </Box>
                            <Typography>
                                O débito será efetuado no terceiro dia útil seguinte ao da contratação. Não havendo a confirmação da cobrança o seguro estará automaticamente cancelado.
                            </Typography>  
                            <Divider 
                                sx={{
                                    my: 2
                                }}
                            />
                            <Box
                                display="flex"
                                alignItems="end"
                                flexDirection="column"
                            >
                                <Box
                                    mb={1}
                                >
                                    <Button
                                        variant="contained"
                                        onClick={
                                            handleSubmit(async (toSave: IFormDadosPagamentoDBC) => {
                                                await definirDadosPagamentoDBC({
                                                    codTipoPagamento: TipoPagamento.DebitoConta,
                                                    codBanco: toSave.codBanco,
                                                    agencia: toSave.agencia,
                                                    digitoAgencia: toSave.digitoAgencia,
                                                    conta: toSave.conta,
                                                    digitoConta: toSave.digitoConta
                                                })
                                            })
                                        }
                                        disabled={!!certificadoState.dtHrDefinicaoDadosBancariosDBC?.get()}
                                        endIcon={<ArrowCircleRightIcon />}
                                    >
                                        Contratar Seguro
                                    </Button>  
                                </Box>
                                {
                                    certificadoState.dtHrDefinicaoDadosBancariosDBC?.get() &&
                                    <Typography variant="caption">
                                        Débito em Conta escolhido em {" "}
                                        {
                                            mostraDeDateISOparaDataPtBr(certificadoState.dtHrDefinicaoDadosBancariosDBC?.get(), "dd/MM/yyyy HH:mm:ss")
                                        }
                                    </Typography>
                                }                                          
                            </Box>  
                        </>
                    }
                    <Paper 
                        sx={{
                            p: 2,
                            width: "fit-content"
                        }}
                    >    
                        <Box display="flex" flexWrap="wrap">                    
                            <Button 
                                sx={{
                                    textTransform: "none",
                                    minWidth: "210px",
                                    mr: 1,
                                    mb: 1
                                }}
                                onClick={() => downloadPdfProposta()}
                                disabled={
                                    watch("codTipoPagamento") === TipoPagamento.DebitoConta &&
                                    !certificadoState.dtHrDefinicaoDadosBancariosDBC?.get()
                                }
                                variant="contained">
                                Gerar proposta
                            </Button>
                            <Button 
                                sx={{
                                    textTransform: "none",
                                    minWidth: "210px",
                                    mb: 1
                                }}
                                component="label"
                                disabled={!certificadoState.dtHrImpressaoProposta?.get()}
                                variant="contained">                                    
                                    Anexar proposta assinada
                                    <input
                                        type="file"
                                        id="upload-proposta-assinada-file"
                                        accept="application/pdf"
                                        hidden
                                        onChange={async ({ target }) => {
                                            await uploadPropostaAssinada(target.files[0]);
                                            target.value = "";
                                        }}
                                    />
                            </Button>
                        </Box>                    
                        { 
                            (certificadoState?.propostasAnexadas?.get() ?? []).length > 0 &&   
                            <>   
                                <TableContainer sx={{marginTop: 2}}>
                                    <Table size="small" >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{fontWeight: "bold", fontSize: "12px"}}>Data</TableCell>
                                                <TableCell sx={{fontWeight: "bold", fontSize: "12px"}}>Nome do arquivo</TableCell>
                                                <TableCell sx={{fontWeight: "bold", fontSize: "12px", textAlign: "center"}}>Ação</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody sx={{border: "none"}}>
                                            {
                                                (certificadoState?.propostasAnexadas?.get() ?? [])
                                                .slice(state.page.get() * state.rowsPerPage.get(), state.page.get() * state.rowsPerPage.get() + state.rowsPerPage.get())
                                                    .map(p =>{
                                                        return(
                                                            <TableRow key={p.idAnexo}>
                                                                <TableCell sx={{fontSize: "12px"}}>{mostraDeDateISOparaDataPtBr(p.dtHrAnexo, "dd/MM/yyyy")}</TableCell>
                                                                <TableCell sx={{fontSize: "12px", maxWidth: "200px"}}>{p.nomeArquivo}</TableCell>
                                                                <TableCell>
                                                                    <IconButton onClick={() => downloadPropostaAssinada(p.idAnexo)}>
                                                                        <DownloadIcon />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        )
                                                    })
                                            }                                
                                        </TableBody>
                                    </Table>    
                                </TableContainer>
                                {
                                    certificadoState?.propostasAnexadas?.get()?.length > 5 &&
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10]}
                                        component="div"
                                        count={certificadoState?.propostasAnexadas?.get().length}
                                        rowsPerPage={state.rowsPerPage.get()}
                                        page={state.page.get()}
                                        labelRowsPerPage="Mostrando por página"
                                        labelDisplayedRows={({ from, to, count }) => 
                                            `${from}–${to} de ${count !== -1 ? count : `${to}`}`                                    
                                        }
                                        onPageChange={(event: unknown, newPage: number) => 
                                            state.page.set(newPage)
                                        }
                                        onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                            state.rowsPerPage.set(+event.target.value);
                                            state.page.set(0);
                                        }}
                                    />
                                }   
                            </>                        
                        }
                    </Paper>
                    <Box 
                        display="flex" 
                        flexDirection="column" 
                        pl={2} 
                        mt={1}
                    >
                        {
                            !certificadoState.dtHrImpressaoProposta?.get() &&
                                <Typography variant="caption">
                                    Status proposta: aguardando impressão e coleta de assinatura
                                </Typography>
                        }
                        {
                            certificadoState.dtHrImpressaoProposta?.get() &&
                                <Typography variant="caption">
                                    <span>Status proposta: impressa em </span>
                                    {
                                        mostraDeDateISOparaDataPtBr(certificadoState.dtHrImpressaoProposta?.get({noproxy: true}), "dd/MM/yyyy HH:mm:ss")
                                    }
                                </Typography>
                        }
                        {
                            certificadoState.dtHrPropostaAnexada?.get() &&
                                <Typography variant="caption">
                                    <span>Status proposta: anexada em </span>
                                    {
                                        mostraDeDateISOparaDataPtBr(certificadoState.dtHrPropostaAnexada?.get({noproxy: true}), "dd/MM/yyyy HH:mm:ss")
                                    }
                                </Typography>
                        }
                    </Box>
                    {
                        watch("codTipoPagamento") === TipoPagamento.CartaoCredito &&
                        <>
                            <Box>
                                <Typography variant="body2" mt={4} pl={2}>
                                Realize o pagamento clicando em Ir para Pagamento. Você será direcionado para um ambiente externo- Pay2b, onde os dados do cartão de crédito para o pagamento do seguro serão solicitados. Aguarde a confirmação e retorne a essa página para concluir a operação.
                                </Typography>
                                <Typography variant="body2" mt={2} pl={2}>
                                    <strong>
                                        Atenção: caso o pagamento não seja efetuado no dia da emissão da proposta, o cooperado está sem cobertura e a mesma é invalidada. Para cobertura deverá ser criada uma nova proposta.
                                    </strong>
                                </Typography>
                            </Box>
                            <Box pl={2} mt={4}>
                                <Button 
                                    sx={{
                                        textTransform: "none",
                                        minWidth: "210px"
                                    }}
                                    disabled={!certificadoState.dtHrImpressaoProposta?.get() || !!certificadoState.dtHrPagamento?.get()}
                                    onClick={async () => {
                                        let urlCheckout = certificadoState.urlCheckout?.get();
        
                                        if (!urlCheckout) {
                                            const response = await pagar();
        
                                            if (response?.success) {
                                                urlCheckout = response.urlCheckout;
                                                setErroGeracaoPagamentoPay2B(false);
                                            }
        
                                            if (response?.success === false) 
                                                setErroGeracaoPagamentoPay2B(true);
                                                
                                        }
                                        if (urlCheckout) {
                                            popupCenter({
                                                url: urlCheckout,
                                                w: 600,
                                                h: 400,
                                                onClose: () => updatePagamentoStatus()
                                            });
                                        }
                                    }}
                                    variant="contained">
                                    Ir para Pagamento
                                </Button>
                            </Box>
                            <Box pl={2} mt={1}>
                                {
                                    !certificadoState.dtHrPagamento?.get() 
                                    ?
                                        <Typography variant="caption">
                                            Status pagamento: aguardando pagamento ({certificadoState.statusPagamento?.get() ?? ""})
                                        </Typography>
                                    :
                                        <Typography variant="caption">
                                            <span>Status Pagamento: Pago </span>
                                            {
                                                mostraDeDateISOparaDataPtBr(certificadoState.dtHrPagamento?.get({noproxy: true}), "dd/MM/yyyy HH:mm:ss")
                                            }
                                        </Typography>
                                }
                                {
                                    erroGeracaoPagamentoPay2B &&
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        Não foi possível acessar o método de pagamento da Seguradora. Tente novamente.
                                    </Alert>
                                }
                                {
                                    !!certificadoState.dtHrPagamento?.get() && !certificadoState.ehEfetivouSigas?.get() &&
                                    <Alert
                                        sx={{ mt: 2 }}
                                        severity="info"
                                        action={
                                            <Button color="inherit" size="small" sx={{ textTransform: "none" }} 
                                                onClick={() => efetivarNoSigas()} >
                                                Reenviar Proposta para o Sigas
                                            </Button>
                                        } >
                                        <AlertTitle>A Proposta não foi efetivada no Sigas.</AlertTitle>
                                        Clique no Botão ao lado para tentar novamente
                                    </Alert>
                                }
                            </Box>
                            <Box>
                                {
                                    certificadoState.dtHrPagamento?.get() &&
                                    <Typography variant="body2" pl={2} mt={4}>
                                        Contratação concluída!  
                                    </Typography>
                                }
                                <Typography variant="body2" mt={!certificadoState.dtHrPagamento?.get() ? 4 : 2} pl={2}>
                                    Imprima o certificado e entregue ao cooperado
                                </Typography>
                            </Box>
                        </> 
                    }
                    <Box pl={2} mt={4}>
                        <Button 
                            sx={{
                                textTransform: "none",
                                minWidth: "210px"
                            }}
                            disabled={
                                (
                                    watch("codTipoPagamento") === TipoPagamento.CartaoCredito &&
                                    !certificadoState.dtHrPagamento?.get()
                                ) ||
                                (
                                    watch("codTipoPagamento") === TipoPagamento.DebitoConta &&
                                    !certificadoState.dtHrDefinicaoDadosBancariosDBC?.get()
                                )
                            }
                            onClick={() => downloadPdfCertificado()}
                            variant="contained">
                            Gerar certificado
                        </Button>
                    </Box>
                    <Box pl={2} mt={1}>
                        {
                            certificadoState.dtHrEmissaoCertificado?.get() &&
                                <Typography variant="caption">
                                    <span>Status Certificado: emitido em </span>
                                    {
                                        mostraDeDateISOparaDataPtBr(certificadoState.dtHrEmissaoCertificado?.get({noproxy: true}), "dd/MM/yyyy HH:mm:ss")
                                    }
                                </Typography>
                        }
                    </Box>
                    <ParteCancelamento />
                </Stack>
            </Box>
        </>
    );
}

export default PassoCertificado;